import { confirmOrder } from '@/api/order-api';
import { toast } from '@/hooks/use-toast';
import { OrderResponse, TOrderWithRelation } from '@/types/order-type';
import { differenceInMinutes, format, formatDistanceToNow, isToday } from 'date-fns';
import { CheckCircle2, DollarSign, Ellipsis, Eye, FileText, UserPlus } from 'lucide-react';
import React, { useRef } from 'react';

/** Max finger/pointer movement (px) still treated as a tap rather than a scroll */
const TAP_TOLERANCE_PX = 10;

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/* -------------------------------------------------------------------------- */
/* Utils                                                                      */
/* -------------------------------------------------------------------------- */

export function formatOrderDate(date: string | Date) {
  const d = new Date(date);
  const mins = differenceInMinutes(new Date(), d);

  if (mins < 1) return 'Now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return formatDistanceToNow(d, { addSuffix: true });
  if (isToday(d)) return 'Today';

  return format(d, 'MMM d');
}

/* -------------------------------------------------------------------------- */
/* Sub Components                                                             */
/* -------------------------------------------------------------------------- */

function OrderStatusBadge({ status }: { status: string }) {
  const statusStyles: Record<string, string> = {
    DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100/80',
    CONFIRMED: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100/80',
    PAID: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100/80',
    CANCELLED: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100/80',
    REFUNDED: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100/80',
    RETURNED: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100/80',
  };

  const styles =
    statusStyles[status] ||
    'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-100/80';
  return (
    <Badge variant="secondary" className={`text-xs shrink-0 border ${styles}`}>
      {status}
    </Badge>
  );
}

function OrderActions({
  order,
  orderWithRelation,
  onConfirm,
  onView,
  onViewInvoice,
  onAssignUser,
}: {
  order: OrderResponse;
  orderWithRelation: TOrderWithRelation;
  onConfirm?: (success: boolean, order: TOrderWithRelation) => void;
  onView?: (order: TOrderWithRelation) => void;
  onViewInvoice?: (order: TOrderWithRelation) => void;
  onAssignUser?: (order: TOrderWithRelation) => void;
}) {
  const handleConfirm = async () => {
    try {
      const res = await confirmOrder(order.id);
      if (!res.success) throw new Error(res.message);
      onConfirm?.(true, orderWithRelation);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to confirm order',
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-accent">
          <Ellipsis className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {order.status === 'DRAFT' && (
          <>
            <DropdownMenuItem onClick={handleConfirm}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Confirm Order
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={() => onView?.(orderWithRelation)}>
          <Eye className="mr-2 h-4 w-4" />
          View Order
        </DropdownMenuItem>

        {onAssignUser && (
          <DropdownMenuItem onClick={() => onAssignUser(orderWithRelation)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Assign To User
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onClick={() => onViewInvoice?.(orderWithRelation)}>
          <FileText className="mr-2 h-4 w-4" />
          Print Receipt
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OrderMeta({
  createdAt,
  totalAmount,
  itemCount,
}: {
  createdAt: string;
  totalAmount: number;
  itemCount: number;
}) {
  return (
    <div className="flex w-full sm:w-auto justify-between sm:justify-end gap-3 text-xs sm:text-sm">
      <span className="text-muted-foreground">{formatOrderDate(createdAt)}</span>

      <span className="flex items-center gap-1 font-medium">
        <DollarSign className="h-3 w-3" />
        {totalAmount}
      </span>

      <span className="text-muted-foreground">{itemCount} items</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main Component                                                             */
/* -------------------------------------------------------------------------- */

interface OrderChatCellProps {
  orderWithRelation: TOrderWithRelation;
  onView?: (order: TOrderWithRelation) => void;
  onConfirm?: (success: boolean, order: TOrderWithRelation) => void;
  onViewInvoice?: (order: TOrderWithRelation) => void;
  onAssignUser?: (order: TOrderWithRelation) => void;
}

export default function OrderChatCell({
  orderWithRelation,
  onView,
  onConfirm,
  onViewInvoice,
  onAssignUser,
}: OrderChatCellProps) {
  const { order, orderItems } = orderWithRelation;

  // Tap tolerance: ignore "clicks" that follow a scroll/drag gesture
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const movedTooFar = useRef(false);

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
    movedTooFar.current = false;
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    const start = pointerStart.current;
    if (!start || movedTooFar.current) return;
    if (
      Math.abs(e.clientX - start.x) > TAP_TOLERANCE_PX ||
      Math.abs(e.clientY - start.y) > TAP_TOLERANCE_PX
    ) {
      movedTooFar.current = true;
    }
  };
  const handleClick = () => {
    if (movedTooFar.current) {
      movedTooFar.current = false;
      return;
    }
    onView?.(orderWithRelation);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerCancel={() => {
        movedTooFar.current = true;
      }}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onView?.(orderWithRelation);
        }
      }}
      className="p-2 sm:p-3 hover:shadow-md transition cursor-pointer border-l-4 border-l-primary/50"
    >
      <div className="flex flex-col gap-2 sm:gap-3">
        {/* Top row: ID + status + actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <span className="font-semibold text-sm truncate">#{order.orderNumber || order.id}</span>
            <OrderStatusBadge status={order.status} />
            {order.followUpUser && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-blue-50 text-blue-700 border-blue-200">
                @{order.followUpUser}
              </Badge>
            )}
          </div>

          {/* Stop propagation so menu clicks (incl. portaled items) don't also open the dialog */}
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <OrderActions
              order={order}
              orderWithRelation={orderWithRelation}
              onConfirm={onConfirm}
              onView={onView}
              onViewInvoice={onViewInvoice}
              onAssignUser={onAssignUser}
            />
          </div>
        </div>

        {/* Bottom row: meta info */}
        <OrderMeta
          createdAt={order.createdAt!}
          totalAmount={order?.totalAmount ?? 0}
          itemCount={orderItems.length}
        />
      </div>
    </Card>
  );
}
